'use client';

/**
 * useProcessorCallbacks Hook
 *
 * Encapsulates all callbacks for the ChargePaymentProcessor:
 * - Processor lifecycle callbacks (status, success, error, cancel)
 * - Processing screen UI callbacks (ok, retry, close, spending limits)
 */

import { useCallback } from 'react';
import type { PaymentProcessingResult, UseChargeProcessingReturn, CompletionResult } from '../types';
import type { UsePurchaseAnalyticsResult } from '../analytics';

// ============================================================================
// Types
// ============================================================================

export interface UseProcessorCallbacksOptions {
  /** Charge processing state manager */
  chargeProcessing: UseChargeProcessingReturn;
  /** Analytics tracker */
  analytics: UsePurchaseAnalyticsResult;
  /** Whether to show result screens (success/error) in the processing UI.
   * When false, results are delegated to parent via onPaymentComplete/onPaymentFailed callbacks. */
  showSuccessScreen: boolean;
  /** Whether to show the error screen in the processing UI.
   * When false, errors are delegated to parent via onPaymentFailed.
   * Defaults to the value of showSuccessScreen. */
  showErrorScreen?: boolean;
  /** Navigation back function */
  goBack: () => void;
  /** Callback when payment completes successfully */
  onPaymentComplete: (result?: CompletionResult) => void;
  /** Callback when payment fails */
  onPaymentFailed: (error: string) => void;
  /** Optional callback for spending limits error */
  onSpendingLimits?: () => void;
  /** Called when purchase succeeds, before user dismisses the success screen */
  onPurchaseSuccess?: () => void;
  /** Resets startSignal so stale signals don't re-trigger after cancel/dismiss */
  resetSignal: () => void;
}

export interface ProcessorCallbacks {
  /** Called when processor status changes */
  handleStatusChange: (status: string, message: string) => void;
  /** Called when payment succeeds */
  handleSuccess: (result: PaymentProcessingResult) => void;
  /** Called when payment fails */
  handleError: (result: PaymentProcessingResult) => void;
  /** Called when payment is cancelled */
  handleCancel: () => void;
  /** Soft dismiss — navigates back without tracking failure */
  handleDismiss: () => void;
}

export interface ProcessingScreenCallbacks {
  /** OK button click on success screen */
  handleOkClick: () => void;
  /** Retry button click on error screen */
  handleRetryClick: () => void;
  /** Close button click on error screen */
  handleCloseClick: () => void;
  /**
   * Spending limits button click. Only defined when an `onSpendingLimits`
   * callback was provided to the hook. When undefined, the screen falls back
   * to a plain "OK" button that dismisses the error via `handleCloseClick`.
   */
  handleSpendingLimitsClick?: () => void;
}

export interface UseProcessorCallbacksReturn {
  /** Callbacks for ChargePaymentProcessor */
  processorCallbacks: ProcessorCallbacks;
  /** Callbacks for PaymentProcessingScreen UI */
  screenCallbacks: ProcessingScreenCallbacks;
}

// ============================================================================
// Hook
// ============================================================================

export const useProcessorCallbacks = ({
  chargeProcessing,
  analytics,
  showSuccessScreen,
  showErrorScreen: showErrorScreenProp,
  goBack,
  onPaymentComplete,
  onPaymentFailed,
  onSpendingLimits,
  onPurchaseSuccess,
  resetSignal
}: UseProcessorCallbacksOptions): UseProcessorCallbacksReturn => {
  const showErrorScreen = showErrorScreenProp ?? showSuccessScreen;
  // Destructure stable methods (each has [] deps in useChargeProcessing)
  // so callbacks below don't depend on the whole chargeProcessing object.
  const {
    updateStatus, setSuccess, setError, reset, state
  } = chargeProcessing;

  // ==========================================================================
  // Processor Callbacks
  // ==========================================================================

  const handleStatusChange = useCallback((status: string, message: string) => {
    updateStatus(status as any, message);
  }, [updateStatus]);

  const handleSuccess = useCallback((result: PaymentProcessingResult) => {
    resetSignal();

    analytics.trackPurchaseCompleted(
      result.pathname,
      result.threeDAuthResult ? { 'ThreeDS Auth Result': result.threeDAuthResult } : undefined
    );

    onPurchaseSuccess?.();

    if (!showSuccessScreen) {
      reset();
      onPaymentComplete({
        okMessage: result.okMessage,
        cashbackAmount: result.cashbackAmount,
        userCreditId: result.userCreditId
      });
      return;
    }

    setSuccess(result);
  }, [resetSignal, setSuccess, reset, analytics, showSuccessScreen, onPaymentComplete, onPurchaseSuccess]);

  const handleError = useCallback((result: PaymentProcessingResult) => {
    resetSignal();

    const errorMsg = result.errorMessage || 'Payment failed';
    const { spendingLimits } = result;
    analytics.incrementRetryCount();

    // Derive reason from API errorType (snake_case → spaces), matching old-flow payPalProcessingFailReason.
    // Payment-kind fallback ('apple pay failed', etc.) is applied inside trackPurchaseFailed.
    const failedReason = result.errorType ? result.errorType.split('_').join(' ') : 'api error';

    analytics.trackPurchaseFailed(failedReason, {
      'payment error message': errorMsg,
      ...(spendingLimits?.length ? { 'spending limits exceeded': spendingLimits, destination: 'jumio' } : {})
    });

    if (!showErrorScreen) {
      reset();
      onPaymentFailed(errorMsg);
      return;
    }

    setError(result);
  }, [resetSignal, setError, reset, analytics, showErrorScreen, onPaymentFailed]);

  const handleCancel = useCallback(() => {
    analytics.trackPurchaseFailed('user canceled');
    resetSignal();
    reset();
    goBack();
  }, [analytics, resetSignal, reset, goBack]);

  const handleDismiss = useCallback(() => {
    resetSignal();
    reset();
    goBack();
  }, [resetSignal, reset, goBack]);

  // ==========================================================================
  // Processing Screen UI Callbacks
  // ==========================================================================

  const handleOkClick = useCallback(() => {
    const { result } = state;
    onPaymentComplete(result ? {
      okMessage: result.okMessage,
      cashbackAmount: result.cashbackAmount,
      userCreditId: result.userCreditId
    } : undefined);
  }, [onPaymentComplete, state.result]);

  const handleRetryClick = useCallback(() => {
    reset();
  }, [reset]);

  const handleCloseClick = useCallback(() => {
    reset();
    onPaymentFailed(state.error?.errorMessage || 'Payment failed');
  }, [reset, onPaymentFailed, state.error]);

  // ==========================================================================
  // Return
  // ==========================================================================

  // `handleSpendingLimitsClick` is just the consumer's `onSpendingLimits`
  // passed through — when undefined, PaymentProcessingScreen renders a plain
  // "OK" button and routes the click through `handleCloseClick`.
  return {
    processorCallbacks: {
      handleStatusChange,
      handleSuccess,
      handleError,
      handleCancel,
      handleDismiss
    },
    screenCallbacks: {
      handleOkClick,
      handleRetryClick,
      handleCloseClick,
      handleSpendingLimitsClick: onSpendingLimits
    }
  };
};
